import type { Agent, Avatar as AvatarValue, AvatarShape } from "@ark-bot/protocol";

import { api } from "../api.js";
import { placement } from "../avatar-frame.js";
import type { AVATAR } from "../text/avatar.js";

/** The palette the mark editor offers; the fallback colour is picked from it too. */
export const AVATAR_COLORS = ["#6ea8fe", "#58d68d", "#f5b041", "#e879a6", "#a78bfa", "#4dd0c1", "#f2726f", "#9bb1d4"] as const;

/** Every outline a face can be cut to, in the order the picker shows them. */
export const AVATAR_SHAPES: readonly { readonly shape: AvatarShape; readonly label: (t: (typeof AVATAR)["en"]) => string }[] = [
  { shape: "circle", label: (t) => t.shapeCircle },
  { shape: "rounded", label: (t) => t.shapeRounded },
  { shape: "square", label: (t) => t.shapeSquare },
  { shape: "oval", label: (t) => t.shapeOval },
  { shape: "hexagon", label: (t) => t.shapeHexagon },
  { shape: "triangle", label: (t) => t.shapeTriangle },
  { shape: "star", label: (t) => t.shapeStar },
  { shape: "heart", label: (t) => t.shapeHeart },
];

/** One or two letters from a name, matching what the host derives (§6.11). */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return "?";
  const first = [...words[0]!][0] ?? "?";
  if (words.length === 1) return first.toUpperCase();
  return `${first}${[...words[words.length - 1]!][0] ?? ""}`.toUpperCase();
}

/** The same stable colour the host would pick, so an unset avatar looks alike everywhere. */
export function colorFor(id: string): string {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

/** The outlines drawn with clip-path; the heart is a curve and comes from the SVG definitions below. */
const CLIP: Partial<Record<AvatarShape, string>> = {
  oval: "ellipse(37% 50% at 50% 50%)",
  triangle: "polygon(50% 2%, 100% 98%, 0% 98%)",
  star: "polygon(50% 2%, 61% 36%, 98% 36%, 68% 58%, 79% 94%, 50% 72%, 21% 94%, 32% 58%, 2% 36%, 39% 36%)",
  hexagon: "polygon(25% 4%, 75% 4%, 100% 50%, 75% 96%, 25% 96%, 0% 50%)",
  heart: "url(#ark-shape-heart)",
};

/** How the outline is cut: a radius for the three plain ones, a clip for the rest. */
export function shapeStyle(shape: AvatarShape, size: number): React.CSSProperties {
  switch (shape) {
    case "circle":
      return { borderRadius: "50%" };
    case "rounded":
      return { borderRadius: Math.max(6, Math.round(size * 0.22)) };
    case "square":
      return { borderRadius: Math.max(2, Math.round(size * 0.05)) };
    default:
      return { clipPath: CLIP[shape] };
  }
}

/** Where a mark's letters sit: lower in a triangle, whose top is a point. */
function labelStyle(shape: AvatarShape, size: number): React.CSSProperties {
  if (shape === "triangle") return { paddingTop: Math.round(size * 0.3), fontSize: Math.round(size * 0.34) };
  if (shape === "star" || shape === "heart") return { fontSize: Math.round(size * 0.3) };
  return { fontSize: Math.round(size * 0.46) };
}

/**
 * How a legacy picture is drawn: as a background scaled by `zoom` and shifted
 * by `x`/`y` between its own edges, exactly as it was saved. A free picture
 * is an image placed by its centre instead (see {@link Picture}).
 */
export function avatarStyle(avatar: AvatarValue): React.CSSProperties {
  if (avatar.kind === "mark") return { background: avatar.color };
  if (avatar.frame === "free") return {};
  const zoom = avatar.zoom ?? 1;
  // 0.5 is centred; the offset moves by half the overflow, so ±1 reaches the edge.
  const position = (offset: number): string => `${Math.round((0.5 + (offset * (zoom - 1)) / (2 * zoom)) * 100)}%`;
  return {
    backgroundImage: `url(${api.blobUrl(avatar.blobId)})`,
    backgroundSize: `${zoom * 100}%`,
    backgroundPosition: `${position(avatar.x ?? 0)} ${position(avatar.y ?? 0)}`,
    backgroundRepeat: "no-repeat",
  };
}

/** A free picture: its centre at the offset, its width `zoom` frames, whatever its own proportions. */
function Picture({ avatar }: { readonly avatar: Extract<AvatarValue, { kind: "image" }> }): React.JSX.Element {
  const at = placement(avatar);
  const centreX = at.left + at.width / 2;
  const centreY = at.top + at.width / 2;
  return <img className="avatar-picture" src={api.blobUrl(avatar.blobId)} alt="" draggable={false} style={{ left: `${centreX * 100}%`, top: `${centreY * 100}%`, width: `${at.width * 100}%` }} />;
}

interface Props {
  readonly name: string;
  readonly id: string;
  readonly avatar?: AvatarValue | undefined;
  readonly size?: number;
  readonly className?: string;
}

/** An agent's face. Never blank: with nothing set it falls back to initials on a colour of the agent's own. */
export function Avatar({ name, id, avatar, size = 28, className }: Props): React.JSX.Element {
  const value: AvatarValue = avatar ?? { kind: "mark", shape: "circle", color: colorFor(id), label: initialsOf(name) };
  const shape: AvatarShape = value.shape ?? "circle";
  const label = value.kind === "mark" ? (value.label != null && value.label.length > 0 ? value.label : initialsOf(name)) : "";
  return (
    <div className={`avatar avatar-${shape}${className == null ? "" : ` ${className}`}`} aria-hidden="true" title={name} style={{ ...avatarStyle(value), ...shapeStyle(shape, size), ...(value.kind === "mark" ? labelStyle(shape, size) : {}), width: size, height: size }}>
      {value.kind === "image" && value.frame === "free" ? <Picture avatar={value} /> : label}
    </div>
  );
}

/**
 * The outlines that are curves rather than corners, defined once for the
 * page; a face cut to one refers to it by id. Rendered by the app's root.
 */
export function AvatarShapeDefs(): React.JSX.Element {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
      <defs>
        <clipPath id="ark-shape-heart" clipPathUnits="objectBoundingBox">
          <path d="M0.5 0.95 C0.42 0.88 0.05 0.63 0.05 0.35 C0.05 0.18 0.18 0.06 0.32 0.06 C0.4 0.06 0.46 0.1 0.5 0.17 C0.54 0.1 0.6 0.06 0.68 0.06 C0.82 0.06 0.95 0.18 0.95 0.35 C0.95 0.63 0.58 0.88 0.5 0.95 Z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function agentAvatar(agent: Pick<Agent, "id" | "name" | "config">, size?: number): React.JSX.Element {
  return <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} {...(size == null ? {} : { size })} />;
}
